import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogView, Button } from '../index'
import { replaceCurrentBillingConfiguration, getBillingConfigurationReplacementProgress } from '../../services'

export const ReplaceBillingConfigModal = ({ 
    open, 
    setOpen, 
    storeId, 
    futureConfigurations = [], 
    onSuccess = () => {},
    onError = () => {}
}) => {
    const { t } = useTranslation()
    const [selectedConfigId, setSelectedConfigId] = useState(null)
    const [isReplacing, setIsReplacing] = useState(false)
    const [progress, setProgress] = useState(0)
    const [progressStatus, setProgressStatus] = useState('')
    const [isCompleted, setIsCompleted] = useState(false)
    const [error, setError] = useState(null)
    const [jobId, setJobId] = useState(null)
    const intervalRef = useRef(null)

    // Reset state when modal opens/closes
    useEffect(() => {
        if (open) {
            resetState()
        } else {
            clearInterval(intervalRef.current)
        }
    }, [open])

    const resetState = () => {
        setSelectedConfigId(null)
        setIsReplacing(false)
        setProgress(0)
        setProgressStatus('')
        setIsCompleted(false)
        setError(null)
        setJobId(null)
        clearInterval(intervalRef.current)
    }

    const handleConfirm = async () => {
        if (!selectedConfigId) return

        setIsReplacing(true)
        setError(null)

        try {
            const response = await replaceCurrentBillingConfiguration(storeId, selectedConfigId)
            
            // Check if it's a concurrency conflict
            if (response.message && response.message.includes('already in progress')) {
                setError(t('billing.replace_config.already_in_progress'))
                setIsReplacing(false)
                return
            }

            // Start progress tracking
            if (response.job_id) {
                setJobId(response.job_id)
                startProgressTracking(response.job_id)
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || t('billing.replace_config.error'))
            setIsReplacing(false)
        }
    }

    const startProgressTracking = (jobId) => {
        intervalRef.current = setInterval(async () => {
            try {
                const status = await getBillingConfigurationReplacementProgress(jobId)
                
                setProgress(status.progress_percentage || 0)
                setProgressStatus(status.status || '')
                
                if (status.completed) {
                    setIsCompleted(true)
                    setIsReplacing(false)
                    clearInterval(intervalRef.current)
                    
                    // Call success callback after a longer delay
                    setTimeout(() => {
                        onSuccess()
                        setOpen(false)
                    }, 3000)
                }
            } catch (err) {
                setError(err.response?.data?.error || err.message || t('billing.replace_config.progress_error'))
                setIsReplacing(false)
                clearInterval(intervalRef.current)
            }
        }, 2000) // Poll every 2 seconds
    }

    const handleClose = () => {
        if (isReplacing) return // Prevent closing during replacement
        setOpen(false)
    }

    const formatDate = (date) => {
        if (!date) return t('generic.undefined')
        const d = new Date(date)
        return d.toLocaleDateString([], {year: '2-digit', month: '2-digit', day: '2-digit'})
    }

    const getProgressStage = (percentage) => {
        if (percentage <= 10) return t('billing.replace_config.stages.validation')
        if (percentage <= 20) return t('billing.replace_config.stages.config_validation')
        if (percentage <= 70) return t('billing.replace_config.stages.database_operations')
        if (percentage <= 90) return t('billing.replace_config.stages.rake_task')
        return t('billing.replace_config.stages.completing')
    }

    return (
        <DialogView open={open} setOpen={handleClose}>
            <div className="w-full">
                
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                        {t('billing.replace_config.title')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {t('billing.replace_config.description')}
                    </p>
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-800">
                            {t('billing.replace_config.warning')}
                        </p>
                    </div>
                </div>

                <div className="px-6 py-4">
                    {!isReplacing && !isCompleted && (
                        <>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('billing.replace_config.select_future_config')}
                                </label>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {futureConfigurations.length === 0 ? (
                                        <p className="text-gray-500 text-sm py-4 text-center">
                                            {t('billing.replace_config.no_future_configs')}
                                        </p>
                                    ) : (
                                        futureConfigurations.map((config) => (
                                            <div
                                                key={config.id}
                                                className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                                                    selectedConfigId === config.id
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                                onClick={() => setSelectedConfigId(config.id)}
                                            >
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        checked={selectedConfigId === config.id}
                                                        onChange={() => setSelectedConfigId(config.id)}
                                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                                    />
                                                    <div className="ml-3 flex-1">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">
                                                                    ID: {config.id}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    {t('generic.from')}: {formatDate(config.valid_from)}
                                                                </p>
                                                            </div>
                                                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                                                {t('billing.future')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                    <p className="text-sm text-red-600">{error}</p>
                                </div>
                            )}
                        </>
                    )}

                    {isReplacing && !isCompleted && (
                        <div className="space-y-4">
                            <div className="text-center">
                                <h4 className="text-lg font-medium text-gray-900 mb-2">
                                    {t('billing.replace_config.replacing')}
                                </h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    {getProgressStage(progress)}
                                </p>
                                
                                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${progress}%` }}
                                    ></div>
                                </div>
                                
                                <p className="text-sm text-gray-600">
                                    {progress}% {t('generic.completed')}
                                </p>
                            </div>
                        </div>
                    )}

                    {isCompleted && (
                        <div className="text-center py-4">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">
                                {t('billing.replace_config.completed')}
                            </h4>
                            <p className="text-sm text-gray-600">
                                {t('billing.replace_config.success_message')}
                            </p>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 flex justify-end space-x-3">
                    {!isReplacing && !isCompleted && (
                        <>
                            <Button
                                type="secondary"
                                onClick={handleClose}
                                disabled={futureConfigurations.length === 0}
                            >
                                {t('generic.cancel')}
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleConfirm}
                                disabled={!selectedConfigId || futureConfigurations.length === 0}
                            >
                                {t('billing.replace_config.confirm')}
                            </Button>
                        </>
                    )}
                    
                    {isCompleted && (
                        <Button
                            type="primary"
                            onClick={() => setOpen(false)}
                        >
                            {t('generic.close')}
                        </Button>
                    )}
                </div>
            </div>
        </DialogView>
    )
}
